import React, {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from "react";
import _ from "underscore";
import SelectableInput from "../../SelectableInput/SelectableInput";
import useGlobalLists from "../../../api/api-hooks/cloud/useGlobalLists";
import EmptyField from "../EmptyField";
import { Field } from "../../../types/TenantInterfaces";

interface SingleSelectRecordProps {
  field: Field;
  recordID: string | number;
  value: any;
  mode?: "rw" | "r";
  onUpdate: (args: {
    field: Field;
    recordID: string | number;
    payload: { [key: string]: any };
  }) => void;
  submitting?: boolean;
  error?: string | null;
  modifyTrigger?: any;
  table?: boolean;
}

const SingleSelectRecord = (
  {
    field,
    recordID,
    value,
    mode,
    onUpdate,
    modifyTrigger,
    table,
  }: SingleSelectRecordProps,
  ref: React.Ref<{ focus: () => void }>
) => {
  const inputRef = useRef<any>(null);
  const [_localError, setLocalError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<{
    [key: string]: any;
  } | null>(value);
  const { globalLists } = useGlobalLists();

  const options = useMemo(() => {
    const sourcesList = field?.SourcesList;
    if (!sourcesList) return [];

    if (
      sourcesList &&
      typeof sourcesList === "string" &&
      sourcesList.includes("Global.Lists.")
    ) {
      const globalListName = sourcesList.split(".")[2];

      if (!globalLists) return [];

      const globalList = _.findWhere(globalLists, { APIName: globalListName });

      return globalList?.Variables ?? [];
    } else if (Array.isArray(sourcesList)) {
      return sourcesList ? sourcesList : [];
    }
    return [];
  }, [field?.SourcesList, value, globalLists]);

  useEffect(() => {
    if (!options || !options.length) {
      return;
    }

    const option = _.findWhere(options, { Value: value });
    setSelectedRecord(option || null);
  }, [options, value]);

  const handleSelect = (newVal: { [key: string]: any }) => {
    const val = newVal.Value;

    if (!val && field.Required) {
      setLocalError("Select a record to continue");
      return;
    }

    setSelectedRecord(newVal);
    save(newVal?.Value || null);
  };

  const handleDeselect = () => {
    setSelectedRecord(null);
    save(null);
  };

  const handleOnClear = () => {
    setSelectedRecord(null);
    save(null);
  };

  const save = (newValue: { [key: string]: any } | null) => {
    if (value === newValue) return;

    onUpdate({
      field,
      recordID,
      payload: {
        [field.APIName as string]: newValue || null,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  return (
    <div className={table ? "px-2" : ""}>
      <SelectableInput
        ref={inputRef}
        options={options}
        labelKey="Name"
        valueKey="Value"
        colorKey="Color"
        value={selectedRecord}
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        onClear={handleOnClear}
        inputDisplayMode="subtle"
        field={field}
        mode={mode}
        modifyTrigger={modifyTrigger}
        sort={false}
        searchable={field?.SourcesList ? field.SourcesList.length > 5 : false}
        empty={<EmptyField table={table} />}
      />
    </div>
  );
};

export default forwardRef(SingleSelectRecord);
